import {
  AttributionData,
  CampaignCodeConfig,
  ConnectionScenarioE,
  CustomerTypeE,
  GreenSelected,
  PlanFuelSelection,
  SALES_DETAIL_KEY,
  SalesDetailsType,
  SalesSessionStoreKeys,
  UserEnergySelection,
  YesNoE,
} from './SalesSessionStore.types';
import { IEnergyOffer, IPlanAvailability } from '../Energy';
import { writeGeolocation } from '@origin-digital/event-dispatcher';
import { IBroadbandOffer } from '../Broadband';

const windowExists = () => typeof window !== 'undefined';

const getKey = <K extends keyof SalesDetailsType>(key: K): SalesDetailsType[K] | undefined => {
  if (windowExists()) {
    const salesDetails = window.sessionStorage.getItem(SALES_DETAIL_KEY);
    if (salesDetails) {
      const salesDetailParsed: SalesDetailsType = JSON.parse(salesDetails);
      return salesDetailParsed[key];
    }
  }
  return undefined;
};

const setKey = <K extends keyof SalesDetailsType>(key: K, value: SalesDetailsType[K]) => {
  if (windowExists()) {
    const salesDetails = window.sessionStorage.getItem(SALES_DETAIL_KEY);
    if (salesDetails) {
      const salesDetailParsed: SalesDetailsType = JSON.parse(salesDetails);
      salesDetailParsed[key] = value;
      window.sessionStorage.setItem(SALES_DETAIL_KEY, JSON.stringify(salesDetailParsed));
    } else {
      window.sessionStorage.setItem(
        SALES_DETAIL_KEY,
        JSON.stringify({
          [key]: value,
        })
      );
    }
  }
};

const removeKey = <K extends keyof SalesDetailsType>(key: K) => {
  if (windowExists()) {
    const salesDetails = window.sessionStorage.getItem(SALES_DETAIL_KEY);
    if (salesDetails) {
      const salesDetailParsed: SalesDetailsType = JSON.parse(salesDetails);
      delete salesDetailParsed[key];
      window.sessionStorage.setItem(SALES_DETAIL_KEY, JSON.stringify(salesDetailParsed));
    }
  }
};

export const clearSalesSessionStore = () => {
  if (windowExists()) {
    window.sessionStorage.removeItem(SALES_DETAIL_KEY);
  }
};

export const getAddressID = (): string | undefined => getKey(SalesSessionStoreKeys.ADDRESS_ID);
export const setAddressID = (addressID: string) =>
  setKey(SalesSessionStoreKeys.ADDRESS_ID, addressID);

export const getOriginAddressId = (): string | undefined =>
  getKey(SalesSessionStoreKeys.ORIGIN_ADDRESS_ID);
export const setOriginAddressId = (originAddressId: string) =>
  setKey(SalesSessionStoreKeys.ORIGIN_ADDRESS_ID, originAddressId);

export const getNMI = (): string | undefined => getKey(SalesSessionStoreKeys.NMI);
export const setNMI = (nmi: string) => setKey(SalesSessionStoreKeys.NMI, nmi);

export const getPlanFuel = (): PlanFuelSelection | undefined =>
  getKey(SalesSessionStoreKeys.PLAN_FUEL);
export const setPlanFuel = (planFuel: PlanFuelSelection) =>
  setKey(SalesSessionStoreKeys.PLAN_FUEL, planFuel);

export const getSelectedPlan = (): IPlanAvailability | undefined =>
  getKey(SalesSessionStoreKeys.SELECTED_PLAN);
export const setSelectedPlan = (selectedPlan: IPlanAvailability) =>
  setKey(SalesSessionStoreKeys.SELECTED_PLAN, selectedPlan);

export const getSelectedPlanV2 = (): IEnergyOffer | undefined =>
  getKey(SalesSessionStoreKeys.SELECTED_PLAN_V2);
export const setSelectedPlanV2 = (selectedPlan: IEnergyOffer) =>
  setKey(SalesSessionStoreKeys.SELECTED_PLAN_V2, selectedPlan);

export const getConnectionScenario = (): ConnectionScenarioE | undefined =>
  getKey(SalesSessionStoreKeys.CONNECTION_SCENARIO);
export const setConnectionScenario = (connectionScenario: ConnectionScenarioE) =>
  setKey(SalesSessionStoreKeys.CONNECTION_SCENARIO, connectionScenario);

export const getGreenOptionsSelected = (): GreenSelected | undefined =>
  getKey(SalesSessionStoreKeys.GREEN_OPTIONS_SELECTED);
export const setGreenOptionsSelected = (greenOptionsSelected: GreenSelected) =>
  setKey(SalesSessionStoreKeys.GREEN_OPTIONS_SELECTED, greenOptionsSelected);

export const getPlanSolar = (): YesNoE | undefined => getKey(SalesSessionStoreKeys.PLAN_SOLAR);
export const setPlanSolar = (planSolar: YesNoE) =>
  setKey(SalesSessionStoreKeys.PLAN_SOLAR, planSolar);

export const getSearchPostcode = (): string | undefined =>
  getKey(SalesSessionStoreKeys.SEARCH_POSTCODE);
export const setSearchPostcode = (searchPostcode: string) => {
  setKey(SalesSessionStoreKeys.SEARCH_POSTCODE, searchPostcode);
  writeGeolocation({ postcode: searchPostcode });
};

export const getCustomerType = (): CustomerTypeE | undefined =>
  getKey(SalesSessionStoreKeys.CUSTOMER_TYPE);
export const setCustomerType = (customerType: CustomerTypeE) =>
  setKey(SalesSessionStoreKeys.CUSTOMER_TYPE, customerType);

export const getDtmState = (): string | undefined => getKey(SalesSessionStoreKeys.DTM_STATE);
export const setDtmState = (dtmState: string) => setKey(SalesSessionStoreKeys.DTM_STATE, dtmState);

export const getReferralCode = (): string | undefined =>
  getKey(SalesSessionStoreKeys.REFERRAL_CODE);
export const setReferralCode = (referralCode: string) =>
  setKey(SalesSessionStoreKeys.REFERRAL_CODE, referralCode);

export const getCampaignCodeConfig = (): CampaignCodeConfig | undefined =>
  getKey(SalesSessionStoreKeys.CAMPAIGN_CODE_CONFIG);
export const setCampaignCodeConfig = (campaignCodeConfig: CampaignCodeConfig) =>
  setKey(SalesSessionStoreKeys.CAMPAIGN_CODE_CONFIG, campaignCodeConfig);

export const getAttribution = (): AttributionData | undefined =>
  getKey(SalesSessionStoreKeys.ATTRIBUTION);

export const setAttribution = (data: AttributionData) =>
  setKey(SalesSessionStoreKeys.ATTRIBUTION, data);

export const getSelectedBroadbandOffer = (): IBroadbandOffer | undefined =>
  getKey(SalesSessionStoreKeys.SELECTED_BROADBAND_OFFER);
export const setSelectedBroadbandOffer = (selectedBroadbandOffer: IBroadbandOffer) =>
  setKey(SalesSessionStoreKeys.SELECTED_BROADBAND_OFFER, selectedBroadbandOffer);

export const getInternetSelected = (): boolean | undefined =>
  getKey(SalesSessionStoreKeys.INTERNET_SELECTED);
export const setInternetSelected = (internetSelected: boolean) =>
  setKey(SalesSessionStoreKeys.INTERNET_SELECTED, internetSelected);

export const getUserEnergySelection = (): UserEnergySelection | undefined =>
  getKey(SalesSessionStoreKeys.USER_ENERGY_SELECTION);
export const setUserEnergySelection = (userEnergySelection: UserEnergySelection) =>
  setKey(SalesSessionStoreKeys.USER_ENERGY_SELECTION, userEnergySelection);

export const removeSearchPostcode = () => {
  removeKey(SalesSessionStoreKeys.SEARCH_POSTCODE);
};

export const removePlanFuel = () => {
  removeKey(SalesSessionStoreKeys.PLAN_FUEL);
};

export const removePlanSolar = () => {
  removeKey(SalesSessionStoreKeys.PLAN_SOLAR);
};

export const removeConnectionScenario = () => {
  removeKey(SalesSessionStoreKeys.CONNECTION_SCENARIO);
};

export const removeReferralCode = () => {
  removeKey(SalesSessionStoreKeys.REFERRAL_CODE);
};

export const getIsCesAddress = (): YesNoE | undefined =>
  getKey(SalesSessionStoreKeys.IS_CES_ADDRESS);
export const setIsCesAddress = (isCesAddress: YesNoE) =>
  setKey(SalesSessionStoreKeys.IS_CES_ADDRESS, isCesAddress);
export const removeIsCesAddress = () => removeKey(SalesSessionStoreKeys.IS_CES_ADDRESS);

export const getCesAddressQuery = (): string | undefined =>
  getKey(SalesSessionStoreKeys.CES_ADDRESS_QUERY);
export const setCesAddressQuery = (cesAddressQuery: string) =>
  setKey(SalesSessionStoreKeys.CES_ADDRESS_QUERY, cesAddressQuery);
export const removeCesAddressQuery = () => removeKey(SalesSessionStoreKeys.CES_ADDRESS_QUERY);

export const getEspecSessionId = (): string | undefined =>
  getKey(SalesSessionStoreKeys.ESPEC_SESSION_ID);
export const setEspecSessionId = (sessionId: string) =>
  setKey(SalesSessionStoreKeys.ESPEC_SESSION_ID, sessionId);
export const removeEspecSessionId = () => removeKey(SalesSessionStoreKeys.ESPEC_SESSION_ID);

export const getAddressEntryEspec = (): object | undefined =>
  getKey(SalesSessionStoreKeys.ADDRESS_ENTRY_ESPEC);
export const setAddressEntryEspec = (addressEntryEspec: object) =>
  setKey(SalesSessionStoreKeys.ADDRESS_ENTRY_ESPEC, addressEntryEspec);
export const removeAddressEntryEspec = () => removeKey(SalesSessionStoreKeys.ADDRESS_ENTRY_ESPEC);

export const getSelectedPlansEspec = (): object | undefined =>
  getKey(SalesSessionStoreKeys.SELECTED_PLANS_ESPEC);
export const setSelectedPlansEspec = (selectedPlansEspec: object) =>
  setKey(SalesSessionStoreKeys.SELECTED_PLANS_ESPEC, selectedPlansEspec);
export const removeSelectedPlansEspec = () => removeKey(SalesSessionStoreKeys.SELECTED_PLANS_ESPEC);

export const getCID = (): string | undefined => getKey(SalesSessionStoreKeys.CID);
export const setCID = (cid: string) => setKey(SalesSessionStoreKeys.CID, cid);
